/**
 * Input validation utility
 */

interface ValidationOptions {
  minLength?: number
  maxLength?: number
  allowedPattern?: RegExp
  blockedChars?: string[]
  blockedPatterns?: string[]
  fieldName?: string
}

const DEFAULT_OPTIONS: ValidationOptions = {
  minLength: 1,
  maxLength: 50,
  allowedPattern: /^[a-zA-Z0-9\s\-_()]+$/,
  blockedChars: ['&', '%', '#', '@'],
  blockedPatterns: ["'", '"', ';', '--', '/*', '../', './', '\\'],
  fieldName: 'Input'
}

/**
 * Validation function for any input field
 */
export function validateInput(input: string, options: ValidationOptions = {}): { isValid: boolean; error?: string; sanitized?: string } {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  
  if (!input || typeof input !== 'string') {
    return { 
      isValid: false, 
      error: `${opts.fieldName} is required` 
    }
  }
  
  const trimmed = input.trim()
  
  // Check length only if minLength is specified
  if (opts.minLength !== undefined && trimmed.length < opts.minLength) {
    return { 
      isValid: false, 
      error: opts.minLength === 0 
        ? `${opts.fieldName} is invalid`
        : `${opts.fieldName} must be at least ${opts.minLength} characters` 
    }
  }
  
  if (trimmed.length > (opts.maxLength || 50)) {
    return { 
      isValid: false, 
      error: `${opts.fieldName} cannot exceed ${opts.maxLength} characters` 
    }
  }
  
  // Whitelist approach
  if (opts.allowedPattern && !opts.allowedPattern.test(trimmed)) {
    return { 
      isValid: false, 
      error: `${opts.fieldName} contains invalid characters` 
    }
  }
  
  // Block specific characters
  if (opts.blockedChars) {
    for (const char of opts.blockedChars) {
      if (trimmed.includes(char)) {
        return { 
          isValid: false, 
          error: `${opts.fieldName} contains invalid characters` 
        }
      }
    }
  }
  
  // Block specific patterns
  if (opts.blockedPatterns) {
    for (const pattern of opts.blockedPatterns) {
      if (trimmed.includes(pattern)) {
        return { 
          isValid: false, 
          error: `${opts.fieldName} contains invalid characters` 
        }
      }
    }
  }
  
  return { 
    isValid: true, 
    sanitized: trimmed 
  }
}

/**
 * Template name validation (wrapper for backward compatibility)
 */
export function validateTemplateName(name: string): { isValid: boolean; error?: string; sanitized?: string } {
  return validateInput(name, {
    ...ValidationPresets.NAME,
    fieldName: 'Template name'
  })
}

/**
 * Predefined validation presets for common use cases
 */
export const ValidationPresets = {
  // For template names, portfolio names, etc.
  NAME: {
    fieldName: 'Name',
    maxLength: 20,
    allowedPattern: /^[a-zA-Z0-9\s\-_()]+$/,
    blockedChars: ['&', '%', '#', '@'],
    blockedPatterns: ["'", '"', ';', '--', '/*', '../', './', '\\']
  },
  
  // For descriptions, comments
  TEXT: {
    fieldName: 'Text',
    maxLength: 255,
    allowedPattern: /^[a-zA-Z0-9\s\-_().,!?/]+$/,
    blockedPatterns: ["'", '"', ';', '--', '/*', '../', './', '\\', '<script', '</script>']
  },
  
  // For numeric IDs, codes
  CODE: {
    fieldName: 'Code',
    maxLength: 20,
    allowedPattern: /^[a-zA-Z0-9\-_]+$/,
    blockedPatterns: ["'", '"', ';', '--', '/*']
  },
  
  // For email-like inputs
  EMAIL: {
    fieldName: 'Email',
    maxLength: 100,
    allowedPattern: /^[a-zA-Z0-9@.\-_]+$/,
    blockedPatterns: ["'", '"', ';', '--', '/*', '../', './', '\\']
  },
  
  // For numeric inputs (decimal numbers, positive/negative)
  NUMBER: {
    fieldName: 'Number',
    maxLength: 20,
    allowedPattern: /^[\-+]?\d*\.?\d+$/,
    blockedPatterns: ["'", '"', ';', '--', '/*', '../', './', '\\']
  },
  
  // For percentage inputs (0-100)
  PERCENTAGE: {
    fieldName: 'Percentage',
    maxLength: 10,
    allowedPattern: /^[+]?\d*\.?\d+$/,
    blockedPatterns: ["'", '"', ';', '--', '/*', '../', './', '\\']
  },
  
  // For integer inputs
  INTEGER: {
    fieldName: 'Integer',
    maxLength: 15,
    allowedPattern: /^[\-+]?\d+$/,
    blockedPatterns: ["'", '"', ';', '--', '/*', '../', './', '\\']
  }
}

/**
 * Simple logging function
 */
export function logValidationEvent(message: string, data: any) {
  console.warn('Validation Event:', message, data)
}

/**
 * Validate numeric input (decimal, positive/negative)
 */
export function validateNumber(input: string | number): { isValid: boolean; error?: string; sanitized?: number } {
  const strInput = typeof input === 'number' ? input.toString() : input
  const result = validateInput(strInput, ValidationPresets.NUMBER)
  
  if (!result.isValid) {
    return { 
      isValid: false, 
      error: result.error
    }
  }
  
  const num = parseFloat(result.sanitized || '')
  if (isNaN(num)) {
    return {
      isValid: false,
      error: 'Invalid number format'
    }
  }
  
  return {
    isValid: true,
    sanitized: num
  }
}

/**
 * Validate percentage input (0-100)
 */
export function validatePercentage(input: string | number): { isValid: boolean; error?: string; sanitized?: number } {
  const strInput = typeof input === 'number' ? input.toString() : input
  const result = validateInput(strInput, ValidationPresets.PERCENTAGE)
  
  if (!result.isValid) {
    return { 
      isValid: false, 
      error: result.error
    }
  }
  
  const num = parseFloat(result.sanitized || '')
  if (isNaN(num) || num < 0 || num > 100) {
    return {
      isValid: false,
      error: 'Percentage must be between 0 and 100'
    }
  }
  
  return {
    isValid: true,
    sanitized: num
  }
}

/**
 * Validate integer input
 */
export function validateInteger(input: string | number): { isValid: boolean; error?: string; sanitized?: number } {
  const strInput = typeof input === 'number' ? input.toString() : input
  const result = validateInput(strInput, ValidationPresets.INTEGER)
  
  if (!result.isValid) {
    return { 
      isValid: false, 
      error: result.error
    }
  }
  
  const num = parseInt(result.sanitized || '', 10)
  if (isNaN(num)) {
    return {
      isValid: false,
      error: 'Invalid integer format'
    }
  }
  
  return {
    isValid: true,
    sanitized: num
  }
}
